/**
 * road_mesh.ts — textured street surfaces for settlements (2026-08-31 wave).
 *
 * Roads were disabled ("graded ribbons vs stamped ground mismatch") because
 * the old path took Y from the plan's gradeProfile. The stamp law makes that
 * moot: the GROUND is already conformed to the road corridor by
 * `stampedHeight`, so these strips DRAPE on `meshHeight` (what you see) and
 * skim just above it — ribbon and ground agree by construction.
 *
 * Texturing practice (research): U runs ACROSS the road, V along it in
 * METRES (tiling independent of edge length — the engine's 0..1 arc UV
 * stretched per edge), shared wrap-repeat textures, a slight polygon offset
 * against z-shimmer. Class → surface: paved concrete for city mains,
 * sci-fi plate for town mains, packed dirt for lanes and hamlets, pavers
 * for the walking strips (the sidewalk's own material reads as the
 * furnishing zone the street furniture stands in).
 */
import * as THREE from 'three/webgpu';
import type { CityPlan, RoadEdge } from 'enginex/city/config.js';
import type { SettlementSite } from '../../../shared/settlements.js';
import { meshHeight } from './terrain_stamp.js';

export interface RoadMaterialSet {
  paved: THREE.Material;
  plate: THREE.Material;
  dirt: THREE.Material;
  walk: THREE.Material;
}

/** World metres one texture tile covers along V (and across U). */
const TILE_M: Record<keyof RoadMaterialSet, number> = { paved: 4, plate: 3.2, dirt: 3, walk: 2.2 };
const ROAD_RAISE = 0.05;
const WALK_RAISE = 0.085;   // a curb's visual step above the carriageway
const STEP_M = 3;           // cross-section sampling along the polyline
const WALK_GAP = 0.15;      // sliver between carriageway edge and walk strip

type Bucket = { positions: number[]; uvs: number[]; indices: number[] };

function bucketOf(m: Map<string, Bucket>, k: string): Bucket {
  let b = m.get(k);
  if (!b) { b = { positions: [], uvs: [], indices: [] }; m.set(k, b); }
  return b;
}

/** Append one draped strip [offA..offB] beside/over the edge into a bucket. */
function stripInto(
  b: Bucket, e: RoadEdge, offA: number, offB: number, raise: number, tile: number,
  seed: string, cx: number, cz: number,
): void {
  const p = e.polyline;
  let acc = 0;
  let rowPrev = -1;
  for (let i = 0; i + 1 < p.length; i += 2) {
    const isLast = i + 3 >= p.length;
    const ax = p[i], az = p[i + 1];
    let dx = 0, dz = 1;
    if (!isLast) {
      const L = Math.hypot(p[i + 2] - ax, p[i + 3] - az);
      if (L < 1e-6) continue;
      dx = (p[i + 2] - ax) / L; dz = (p[i + 3] - az) / L;
    } else if (i >= 2) {
      const L = Math.hypot(ax - p[i - 2], az - p[i - 1]);
      dx = L > 0 ? (ax - p[i - 2]) / L : 0; dz = L > 0 ? (az - p[i - 1]) / L : 1;
    }
    const segL = isLast ? 0 : Math.hypot(p[i + 2] - ax, p[i + 3] - az);
    const steps = isLast ? 1 : Math.max(1, Math.round(segL / STEP_M));
    for (let sIdx = 0; sIdx < steps; sIdx++) {
      const t = isLast ? 0 : sIdx / steps;
      const x = ax + (isLast ? 0 : (p[i + 2] - ax) * t);
      const z = az + (isLast ? 0 : (p[i + 3] - az) * t);
      const v = (acc + (isLast ? 0 : segL * t)) / tile;
      const px = -dz, pz = dx; // left perpendicular
      const lx = x + px * offA, lz = z + pz * offA;
      const rx = x + px * offB, rz = z + pz * offB;
      const ly = meshHeight(seed, cx + lx, cz + lz) + raise;
      const ry = meshHeight(seed, cx + rx, cz + rz) + raise;
      const base = b.positions.length / 3;
      b.positions.push(lx, ly, lz, rx, ry, rz);
      b.uvs.push(0, v, Math.abs(offB - offA) / tile, v);
      if (rowPrev >= 0) {
        b.indices.push(rowPrev, base, rowPrev + 1, base, base + 1, rowPrev + 1);
      }
      rowPrev = base;
    }
    acc += segL;
  }
}

/**
 * Build the settlement's street-surface group (LOCAL coords — position the
 * group at the site center). Geometry only references the shared materials;
 * teardown disposes geometries and leaves the materials alone.
 */
export function buildRoadGroup(
  plan: CityPlan, site: SettlementSite, seed: string, mats: RoadMaterialSet,
): THREE.Group {
  const group = new THREE.Group();
  group.name = `roads:${site.id}`;
  const buckets = new Map<string, Bucket>();
  const tier = site.tier;
  const surfaceOf = (cls: RoadEdge['roadClass']): keyof RoadMaterialSet => {
    if (tier === 'hamlet') return 'dirt';
    if (tier === 'town') return cls === 'arterial' ? 'plate' : cls === 'collector' ? 'dirt' : 'dirt';
    return cls === 'local' ? 'walk' : 'paved'; // city: mains paved, lanes paver
  };
  const sidewalkOn = (cls: RoadEdge['roadClass']): boolean =>
    tier === 'city' ? cls !== 'local' : tier === 'town' ? cls === 'arterial' : false;
  const sw = plan.config.roads.sidewalkWidth;
  for (const e of plan.roads.edges) {
    const surf = surfaceOf(e.roadClass);
    stripInto(bucketOf(buckets, surf), e, e.halfWidth, -e.halfWidth, ROAD_RAISE, TILE_M[surf], seed, site.centerX, site.centerZ);
    if (sidewalkOn(e.roadClass) && sw > 0.5) {
      const wb = bucketOf(buckets, 'walk');
      stripInto(wb, e, e.halfWidth + WALK_GAP, e.halfWidth + WALK_GAP + sw, WALK_RAISE, TILE_M.walk, seed, site.centerX, site.centerZ);
      stripInto(wb, e, -e.halfWidth - WALK_GAP, -e.halfWidth - WALK_GAP - sw, WALK_RAISE, TILE_M.walk, seed, site.centerX, site.centerZ);
    }
  }
  for (const [k, b] of buckets) {
    if (b.indices.length === 0) continue;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(b.positions, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(b.uvs, 2));
    geo.setIndex(b.indices);
    geo.computeVertexNormals();
    const mesh = new THREE.Mesh(geo, mats[k as keyof RoadMaterialSet]);
    mesh.receiveShadow = true;
    mesh.renderOrder = k === 'walk' ? 2 : 1;
    group.add(mesh);
  }
  return group;
}
